/* eslint-disable consistent-return */

import { Mongo } from 'meteor/mongo';
import SimpleSchema from 'simpl-schema';
import { includes, values, pick, map } from 'lodash';

import requiredIf from '../../modules/schema-conditional-required';

export const ELEMENT_TYPES = {
  INPUT: 'input',
  SELECT: 'select',
  RADIO: 'radio',
  IMAGE: 'image',
  VIDEO: 'video',
  LINK: 'link',
  ICON: 'icon',
  TEXTAREA: 'textarea',
  IFRAME: 'iframe',
  IMAPS: 'imaps',
  POINTCLOUD: 'pointcloud',
  BUTTON: 'button',
  UPLOADFILE: 'uploadfile',
  STREETVIEW: 'streetview',
  CATASTRO: 'catastro',
  MULTIPLEIMAPS: 'multipleimaps',
  MULTIPLEPICTURES: 'multiplepictures',
  MULTIPLEVIDEOS: 'multiplevideos',
  MULTIPLEIFRAME: 'multipleiframe',
  MULTIPLEPOINTCLOUD: 'multiplepointcloud',
  MULTIPLEBUTTON: 'multipleButton',
  MULTIPLESELECTOR: 'multipleselector',
  properties: {
    INPUT: {
      name: 'INPUT',
      value: 'input',
      humanValue: 'Input Field'
    },
    SELECT: {
      name: 'SELECT',
      value: 'select',
      humanValue: 'Dropdown Field'
    },
    RADIO: {
      name: 'RADIO',
      value: 'radio',
      humanValue: 'Checkbox Field'
    },
    IMAGE: {
      name: 'IMAGE',
      value: 'image',
      humanValue: 'Picture Field'
    },
    VIDEO: {
      name: 'VIDEO',
      value: 'video',
      humanValue: 'Video Field'
    },
    LINK: {
      name: 'LINK',
      value: 'link',
      humanValue: 'Link Field'
    },
    ICON: {
      name: 'ICON',
      value: 'icon',
      humanValue: 'Icon Field'
    },
    TEXTAREA: {
      name: 'TEXTAREA',
      value: 'textarea',
      humanValue: 'Text Area'
    },
    IFRAME: {
      name: 'IFRAME',
      value: 'iframe',
      humanValue: 'Iframe'
    },
    IMAPS: {
      name: 'IMAPS',
      value: 'imaps',
      humanValue: 'IMaps'
    },
    POINTCLOUD: {
      name: 'POINTCLOUD',
      value: 'pointcloud',
      humanValue: 'Point Cloud'
    },
    BUTTON: {
      name: 'BUTTON',
      value: 'button',
      humanValue: 'Button'
    },
    UPLOADFILE: {
      name: 'UPLOADFILE',
      value: 'uploadfile',
      humanValue: 'Upload file'
    },
    STREETVIEW: {
      name: 'STREETVIEW',
      value: 'streetview',
      humanValue: 'Street View'
    },
    CATASTRO: {
      name: 'CATASTRO',
      value: 'catastro',
      humanValue: 'Catastro'
    },
    MULTIPLEIMAPS: {
      name: 'MULTIPLEIMAPS',
      value: 'multipleimaps',
      humanValue: 'Multiple Imaps'
    },
    MULTIPLEPICTURES: {
      name: 'MULTIPLEPICTURES',
      value: 'multiplepictures',
      humanValue: 'Multiple Images'
    },
    MULTIPLEVIDEOS: {
      name: 'MULTIPLEVIDEOS',
      value: 'multiplevideos',
      humanValue: 'Multiple Videos'
    },
    MULTIPLEIFRAME: {
      name: 'MULTIPLEIFRAME',
      value: 'multipleiframe',
      humanValue: 'Multiple Iframe'
    },
    MULTIPLEPOINTCLOUD: {
      name: 'MULTIPLEPOINTCLOUD',
      value: 'multiplepointcloud',
      humanValue: 'Multiple Point Cloud'
    },
    MULTIPLEBUTTON: {
      name: 'MULTIPLEBUTTON',
      value: 'multipleButton',
      humanValue: 'Multiple Button'
    },
    MULTIPLESELECTOR: {
      name: 'MULTIPLESELECTOR',
      value: 'multipleselector',
      humanValue: 'Multiple Selector'
    }
  }
};

export const LIBRARY_ICON_TYPES = {
  FONTAWESOME: 'fontawesome',
  SVG: 'svg',
  MATERIAL: 'material'
};

export const MODES_TEMPLATES = {
  PREVIEW: ' preview',
  EDITION: 'edition'
};

export const CAPTION_TYPES = pick(ELEMENT_TYPES, [
  'IMAGE',
  'VIDEO',
  'LINK',
  'ICON',
  'IFRAME',
  'BUTTON',
  'UPLOADFILE',
  'STREETVIEW',
  'TEXTAREA',
  'MULTIPLEIFRAME',
  'MULTIPLEPICTURES',
  'MULTIPLEVIDEOS',
  'MULTIPLEIFRAME',
  'MULTIPLEPOINTCLOUD'
]);

export const LABEL_TYPES = pick(ELEMENT_TYPES, ['INPUT', 'SELECT', 'RADIO', 'TEXTAREA']);

export const OPTION_LIST_TYPES = pick(ELEMENT_TYPES, ['SELECT', 'RADIO']);

export const ICON_TYPES = pick(ELEMENT_TYPES, ['ICON', 'STREETVIEW']);

export const OPTION_LIST_MULTITEMPLATES = pick(ELEMENT_TYPES, [
  'MULTIPLEPICTURES',
  'MULTIPLEVIDEOS',
  'MULTIPLEIFRAME',
  'MULTIPLEBUTTON',
  'MULTIPLESELECTOR'
]);

export const LIST_MULTI_BASIC_EDIT = [
  'imaps',
  'multipleimaps',
  'multipleButton',
  'pointcloud',
  'multiplepointcloud'
];

const AnnotationTemplates = new Mongo.Collection('AnnotationTemplates');

AnnotationTemplates.allow({
  insert: () => false,
  update: () => false,
  remove: () => false
});

AnnotationTemplates.deny({
  insert: () => true,
  update: () => true,
  remove: () => true
});

const iconTemplateSchema = new SimpleSchema({
  library: {
    type: String,
    label: 'The library icon for the element',
    allowedValues: map(LIBRARY_ICON_TYPES, lib => lib)
  },
  icon: {
    type: String,
    label: 'Name icon for the element'
  },
  linkSvg: {
    type: String,
    label: 'Link of svg icon for the element',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('library').value === LIBRARY_ICON_TYPES.SVG,
        schemaInstance: this
      });
    }
  },
  categorySvg: {
    type: String,
    label: 'Category of svg icon for the element',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('library').value === LIBRARY_ICON_TYPES.SVG,
        schemaInstance: this
      });
    }
  }
});

const tagSchema = new SimpleSchema({
  id: {
    type: String,
    label: 'The id of the associated tag'
  },
  text: {
    type: String,
    label: 'The name of the associated tag'
  }
});

const mapTemplateSchema = new SimpleSchema({
  mapId: {
    type: String,
    label: 'The id of the map selected',
    optional: true
  },
  coordinates: {
    type: Array,
    label: 'The coordinates of the annotation'
  },
  'coordinates.$': {
    type: Number,
    label: 'The coordinates of the annotation'
  },
  zoom: {
    type: Number,
    label: 'The zoom for the map'
  },
  linkMap: {
    type: String,
    label: 'The url of shared map',
    optional: true
  },
  centerMode: {
    type: String,
    label: 'Center mode selected',
    optional: true
  },
  coordsCenter: {
    type: Array,
    label: 'The coordinates for center the map',
    optional: true
  },
  'coordsCenter.$': {
    type: Number,
    label: 'The coordinates for center the map'
  },
  heightMap: {
    type: String,
    label: 'The height of map'
  },
  imgMap: {
    type: String,
    label: 'The image of base map',
    optional: true
  },
  nameMap: {
    type: String,
    label: 'The name of map'
  },
  onlyFullScreen: {
    type: Boolean,
    label: 'The option to view only in full screen ',
    optional: true
  }
});

const multiValuesTemplateSchema = new SimpleSchema({
  url: {
    type: String,
    label: 'The link for the picture/video',
    optional: true
  },
  name: {
    type: String,
    label: 'The name for the picture/video'
  },
  imgLink: {
    type: String,
    label: 'The img for the video',
    optional: true
  },
  height: {
    type: String,
    label: 'The height for the iframe',
    optional: true
  },
  width: {
    type: String,
    label: 'The width for the iframe',
    optional: true
  },
  onlyFullScreen: {
    type: Boolean,
    label: 'The option to view only in full screen ',
    optional: true
  },
  isDownload: {
    type: Boolean,
    label: 'Option is show to download file',
    optional: true
  },
  functionSelected: {
    type: Object,
    label: 'The function of jupyter scripts to button',
    optional: true,
    blackbox: true
  },
  centerMode: {
    type: String,
    label: 'Center mode selected',
    optional: true
  },
  coordsCenter: {
    type: Array,
    label: 'The coordinates for center the map',
    optional: true
  },
  'coordsCenter.$': {
    type: Number,
    label: 'The coordinates for center the map'
  }
});

const connectionsTemplateSchema = new SimpleSchema({
  server: {
    type: Object,
    label: 'The server of point cloud/Catastro',
    blackbox: true
  },
  source: {
    type: Object,
    label: 'The source of point cloud/Catastro',
    blackbox: true
  },
  connector: {
    type: Object,
    label: 'The connector of point cloud/Catastro',
    blackbox: true,
    optional: true
  }
});

const settignsElementSchema = new SimpleSchema({
  environment: {
    type: String,
    allowedValues: ['gmv', 'maps', 'NotesComponent'],
    label: 'The environment to apply configuration'
  },
  onlyView: {
    type: Boolean,
    label: 'Is only view mode or not'
  }
});

const elementSchema = new SimpleSchema({
  row: {
    type: Number,
    label: 'The row number this element is located in the layout',
    min: 1
  },
  column: {
    type: Number,
    label:
      'The column number this element is located inside the row. The total number of columns has to be a multiple of 12',
    min: 1,
    max: 12
  },
  order: {
    type: Number,
    label: 'The order this element should appear inside the column',
    min: 1
  },
  elementKey: {
    type: String,
    label: 'Unique identifier for the element, no spaces or accents'
  },
  elementType: {
    type: String,
    label: 'The type of the form control element that is going to be used',
    allowedValues: map(ELEMENT_TYPES.properties, 'value')
  },
  optionList: {
    type: Array,
    label: 'The list of choices to use in select and radio elements',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(values(OPTION_LIST_TYPES), this.field('elementType').value),
        schemaInstance: this
      });
    }
  },
  'optionList.$': {
    type: String,
    label: 'An item in the radio or select list'
  },
  dataType: {
    type: String,
    label: 'The type of data for the input box',
    allowedValues: ['text', 'number', 'date', 'time', 'datetime-local'],
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('elementType').value === ELEMENT_TYPES.INPUT,
        schemaInstance: this
      });
    }
  },
  labelName: {
    type: String,
    label: 'For input/select/radio this is the name of the label. Set by admin',
    optional: true
  },
  isShowLabelName: {
    type: Boolean,
    label: 'Select is show or not label name',
    optional: true
  },
  caption: {
    type: String,
    label:
      'For image/video/link/icon this is the text that goes along with the image/video/link/icon. Set by user',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(values(CAPTION_TYPES), this.field('elementType').value),
        schemaInstance: this
      });
    }
  },
  isShowCaption: {
    type: Boolean,
    label: 'Show caption',
    optional: true
  },
  value: {
    type: SimpleSchema.oneOf(String, Number),
    label:
      'The value entered by the user for this element. For input/select/radio is a string. For image/video/link/icon/iframe is the URL associated with the element',
    optional: true
  },
  iconKey: {
    type: String,
    label: 'The font-awesome icon string for the element',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(values(ICON_TYPES), this.field('elementType').value),
        schemaInstance: this
      });
    }
  },
  iconElement: {
    type: iconTemplateSchema,
    label: 'The icon to element',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(values(ICON_TYPES), this.field('elementType').value),
        schemaInstance: this
      });
    }
  },
  height: {
    type: Number,
    label: 'The height in pixels for the element',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(values(CAPTION_TYPES), this.field('elementType').value),
        schemaInstance: this
      });
    }
  },
  width: {
    type: Number,
    label: 'The width in pixels for the element',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(values(CAPTION_TYPES), this.field('elementType').value),
        schemaInstance: this
      });
    }
  },
  hiperLink: {
    type: String,
    label: 'Hiperlink for image',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(values(CAPTION_TYPES), this.field('elementType').value),
        schemaInstance: this
      });
    }
  },
  fullScreenIpsilumEnv: {
    type: Boolean,
    label: 'View link in full screen inside ipsilum enviroment or outside',
    optional: true
  },
  enabledFullScreen: {
    type: Boolean,
    label: 'Option to enable o disabled full screen button',
    optional: true
  },
  positionMultiTemplates: {
    type: String,
    label: 'Option to position elements',
    optional: true
  },
  positionElement: {
    type: String,
    label: 'Option to position element value/link/image...',
    optional: true
  },
  positionLabelName: {
    type: String,
    label: 'Option to position labelNameElements',
    optional: true
  },
  isDownload: {
    type: Boolean,
    label: 'Option is show to download file',
    optional: true
  },
  map: {
    type: mapTemplateSchema,
    label: 'The map selected',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('elementType').value === ELEMENT_TYPES.IMAPS,
        schemaInstance: this
      });
    }
  },
  connections: {
    type: connectionsTemplateSchema,
    label: 'Connections selected',
    optional: true
  },
  multipleMaps: {
    type: Array,
    label: 'Multiple maps selected',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('elementType').value === ELEMENT_TYPES.MULTIPLEIMAPS,
        schemaInstance: this
      });
    }
  },
  'multipleMaps.$': {
    type: mapTemplateSchema,
    label: 'Multiple maps selected'
  },
  multiValues: {
    type: Array,
    label: 'Multiple values selected',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(values(OPTION_LIST_MULTITEMPLATES)),
        schemaInstance: this
      });
    }
  },
  'multiValues.$': {
    type: multiValuesTemplateSchema,
    label: 'Multiple values selected'
  },
  multiplePointCloud: {
    type: Array,
    label: 'Multiple Point cloude selected',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('elementType').value === ELEMENT_TYPES.MULTIPLEPOINTCLOUD,
        schemaInstance: this
      });
    }
  },
  'multiplePointCloud.$': {
    type: connectionsTemplateSchema,
    label: 'Multiple Point cloud selected'
  },
  settings: {
    type: Array,
    label: 'Configuration to choose if mode edition or only view',
    optional: true
  },
  'settings.$': {
    type: settignsElementSchema,
    label: 'Multiple Point cloud selected'
  }
});

AnnotationTemplates.schema = new SimpleSchema({
  owner: {
    type: String,
    label: 'The ID of the user this annotation template belongs to'
  },
  createdAt: {
    type: String,
    label: 'The date this annotation template was created in the database',
    autoValue() {
      if (this.isInsert) return new Date().toISOString();
    }
  },
  updatedAt: {
    type: String,
    label: 'The date this annotation template was last updated',
    autoValue() {
      if (this.isInsert || this.isUpdate) return new Date().toISOString();
    }
  },
  deleted: {
    type: String,
    label: 'The date the annotation template was deleted',
    autoValue() {
      if (this.isInsert) return 'no';
    }
  },
  name: {
    type: String,
    label: 'A name to identify the annotation template'
  },
  libraryIcon: {
    type: String,
    label: 'The library icon string for the annotation template',
    optional: true
  },
  icon: {
    type: String,
    label: 'The font-awesome icon string for the annotation template'
  },
  iconSvg: {
    type: String,
    label: 'The font-awesome icon string for the annotation template',
    optional: true
  },
  categorySvg: {
    type: String,
    label: 'The font-awesome icon string for the annotation template',
    optional: true
  },
  tags: {
    type: Array,
    label: 'The tags associated to the annotation template'
  },
  'tags.$': {
    type: tagSchema,
    label: 'The tags associated to the annotation template'
  },
  elements: {
    type: Array,
    label:
      'This array constaints all the elements and thiri configuration and layout info for the template'
  },
  'elements.$': {
    type: elementSchema,
    label: 'An layout element in the template'
  },
  isBase: {
    type: Boolean,
    label: 'Is base template',
    optional: true
  },
  templateBaseId: {
    type: String,
    label: 'The id of template base asociated to template custom',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('isBase').value === false,
        schemaInstance: this
      });
    }
  }
});

AnnotationTemplates.attachSchema(AnnotationTemplates.schema);

export default AnnotationTemplates;
